import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenWelcome } from '@gitbook/app-navigation';
import { webConfig } from '@gitbook/env';
import { toast } from '@gitbook/spine-react';
import { useHeadTitle } from '@gitbook/util-react-head';
import { normalizeURLSlug } from '@gitbook/util-url';

import { createAndWaitForOpenAPISpec } from '../../Components';
import {
    apiClient,
    apiQuery,
    apiQueryClient,
    clearOnboardingParam,
    getOnboardingParam,
    OnboardingParamName,
    useUserOrganizationsToJoin,
} from '../../state';
import { CreateOrganizationScreen } from './CreateOrganizationScreen';
import { OrganizationsListScreen } from './OrganizationsListScreen';
import type { PendingOrganization } from './useFlowContext';
import { createOnboardingSite } from './utils';

/**
 * First screen as part of the user onboarding.
 * Also used when creating an organization.
 */
export default function WelcomeScreen(props: {
    screen: NavigationScreenWelcome;

    // flow controls
    onNext: ({
        organization,
        siteId,
        hasBootstrappedContent,
    }: {
        organization: api.Organization;
        siteId: string;
        hasBootstrappedContent?: boolean;
    }) => void;
    onNextSiteOnboarding: (pendingOrganization: PendingOrganization) => void;
    onTitleChange?: (title: string) => void;
}): React.ReactElement {
    const { screen, onNext, onNextSiteOnboarding, onTitleChange } = props;
    const organizations = useUserOrganizationsToJoin();
    const [isCreatingNewOrg, setIsCreatingNewOrg] = React.useState(false);

    // Joining an existing organization skips site onboarding (it's only asked when
    // setting up a brand new site), so we create the site (and import the spec) here.
    async function handleJoinOrg({ organization }: { organization: api.Organization }) {
        await syncCreatedOrganizationQueries(organization);

        const site = await createOnboardingSite({ organization });

        if (!site) {
            return;
        }

        const openapiOnboardingParam = getOnboardingParam(OnboardingParamName.OPENAPI);
        const hasBootstrappedContent = openapiOnboardingParam
            ? await importOpenApiSpecInSite({
                  organization,
                  site,
                  openApiSpecURL: openapiOnboardingParam,
              })
            : false;

        onNext({ organization, siteId: site.id, hasBootstrappedContent });
    }

    // The reason for upgrading is passed as a hash parameter
    const reason = screen.hash;
    const shouldShowOrganizationsList =
        !screen.organizationType && organizations.length > 0 && !isCreatingNewOrg;

    useHeadTitle('Welcome');

    if (shouldShowOrganizationsList) {
        return (
            <OrganizationsListScreen
                organizations={organizations}
                onClickCreateOrg={() => setIsCreatingNewOrg(true)}
                onNext={handleJoinOrg}
            />
        );
    }

    return (
        <CreateOrganizationScreen
            reason={reason}
            onNext={onNextSiteOnboarding}
            onTitleChange={onTitleChange}
        />
    );
}

/**
 * Keep organization queries in sync after creating an organization so the app
 * can immediately resolve org context during the transition to `/o/{orgId}/welcome`.
 */
export async function syncCreatedOrganizationQueries(organization: api.Organization) {
    // Seed the org detail query so the org-scoped welcome route can resolve context immediately.
    apiQueryClient.setQueryData(
        apiQuery('getOrganizationById', { organizationId: organization.id }).queryKey,
        organization
    );
    // Keep the current membership list in sync so the org-scoped welcome route does not briefly
    // hit the organization access fallback before the invalidated list refetches.
    apiQueryClient.setQueryData(
        apiQuery('listOrganizationsForAuthenticatedUser', { limit: 100 }).queryKey,
        (data: api.OperationSpec<'listOrganizationsForAuthenticatedUser'>['ResponseBody']) => {
            if (!data || data.items.some((item) => item.id === organization.id)) {
                return data;
            }

            return {
                ...data,
                items: [...data.items, organization],
            };
        }
    );
    // Refresh every org-list query variations so membership checks include the newly-created organization.
    const orgsListAPIRoute = apiQuery('listOrganizationsForAuthenticatedUser', {}).queryKey[0];
    await apiQueryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === orgsListAPIRoute,
    });
}

async function importOpenApiSpecInSite({
    organization,
    site,
    openApiSpecURL,
}: {
    organization: api.Organization;
    site: api.Site;
    openApiSpecURL: string;
}): Promise<boolean> {
    try {
        const apiReferenceTemplate = webConfig.contentTemplates.find(
            (template) => template.id === 'builtin:api-reference'
        );

        if (!apiReferenceTemplate) {
            throw new Error('API Reference template not found');
        }

        const slug = normalizeURLSlug(site.title);

        // Create a computed openapi space and import the OpenAPI spec
        const [{ data: space }] = await Promise.all([
            apiClient.spaces.createSpace(organization.id, {
                computedSource: {
                    type: 'builtin:openapi',
                    props: {
                        models: true,
                        downloadLink: true,
                    },
                    dependencies: {
                        spec: {
                            ref: {
                                kind: 'openapi',
                                spec: slug,
                            },
                        },
                    },
                },
                emoji: 'emoji' in apiReferenceTemplate ? apiReferenceTemplate.emoji : undefined,
                title: apiReferenceTemplate.title,
            }),
            apiReferenceTemplate.openAPISpec
                ? createAndWaitForOpenAPISpec({
                      openAPISpec: {
                          slug,
                          sourceURL: openApiSpecURL,
                      },
                      organizationId: organization.id,
                  })
                : null,
        ]);

        clearOnboardingParam(OnboardingParamName.OPENAPI);

        // Add the new space to the site
        await apiClient.siteSpaces.addSpaceToSite(
            organization.id,
            site.id,
            {
                spaceId: space.id,
            },
            { returnPreference: 'minimal' }
        );
        return true;
    } catch (error) {
        toast.error(
            'Error while importing the OpenAPI spec',
            error instanceof Error ? error : null
        );
        return false;
    }
}
